import React, { useState, useEffect } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';
import { compararOrcamentos, formatarMoeda } from '../../services/orcamentoService';
import toast from 'react-hot-toast';

const ComparacaoPrecos = ({ drinkId, quantidade = 1 }) => {
  const [comparacao, setComparacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);

  useEffect(() => {
    if (drinkId) {
      carregarComparacao();
    }
  }, [drinkId, quantidade]);

  const carregarComparacao = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const resultado = await compararOrcamentos(drinkId, quantidade);
      setComparacao(resultado);
    } catch (err) {
      console.error('Erro ao comparar preços:', err);
      setError('Não foi possível carregar a comparação de preços');
      toast.error('Erro ao comparar preços');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <DynamicIcon name="loader" size={24} className="animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Calculando preços...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center text-red-600 mb-4">
          <DynamicIcon name="alertCircle" size={20} className="mr-2" />
          <span>{error}</span>
        </div>
        <button
          onClick={carregarComparacao}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!comparacao) return null;

  const { standard, premium, diferenca, percentual } = comparacao;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <DynamicIcon name="barChart3" size={20} className="mr-2 text-blue-600" />
        Comparação de Preços
      </h3>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Standard */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-green-800 flex items-center">
              <DynamicIcon name="check" size={16} className="mr-1 text-green-600" />
              Standard
            </h4>
            <span className="text-lg font-bold text-green-700">
              {formatarMoeda(standard.custoTotal)}
            </span>
          </div>
          <p className="text-sm text-green-700">
            Opção com marcas convencionais
          </p>
        </div>

        {/* Premium */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-amber-800 flex items-center">
              <DynamicIcon name="star" size={16} className="mr-1 text-amber-600" />
              Premium
            </h4>
            <span className="text-lg font-bold text-amber-700">
              {formatarMoeda(premium.custoTotal)}
            </span>
          </div>
          <p className="text-sm text-amber-700">
            Opção com marcas premium
          </p>
        </div>
      </div>

      {/* Diferença */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-medium text-blue-800">Diferença</h4>
          <span className="font-bold text-blue-700">
            {formatarMoeda(diferenca)} ({percentual}%)
          </span>
        </div>
        <p className="text-sm text-blue-700">
          A opção Premium é {percentual}% mais cara que a Standard
        </p>
      </div>

      {/* Toggle Detalhes */}
      <button
        onClick={() => setMostrarDetalhes(!mostrarDetalhes)}
        className="flex items-center text-blue-600 hover:text-blue-800"
      >
        <DynamicIcon 
          name={mostrarDetalhes ? "chevronUp" : "chevronDown"} 
          size={16} 
          className="mr-1" 
        />
        {mostrarDetalhes ? "Ocultar detalhes" : "Mostrar detalhes"}
      </button>

      {/* Detalhes */}
      {mostrarDetalhes && (
        <div className="mt-4 border-t pt-4">
          <h4 className="font-medium mb-3">Detalhes dos Ingredientes</h4>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ingrediente
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qtd.
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-green-600 uppercase tracking-wider">
                    Standard
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-amber-600 uppercase tracking-wider">
                    Premium
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-blue-600 uppercase tracking-wider">
                    Dif.
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {standard.detalhes.map((item, index) => {
                  const premiumItem = premium.detalhes[index];
                  const diferenca = premiumItem.custo - item.custo;
                  const percentual = item.custo > 0 
                    ? (diferenca / item.custo * 100).toFixed(0)
                    : 0;
                  
                  return (
                    <tr key={index}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.ingrediente}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                        {item.quantidade} {item.unidade}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600">
                        {formatarMoeda(item.custo)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-amber-600">
                        {formatarMoeda(premiumItem.custo)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-blue-600">
                        +{percentual}%
                      </td>
                    </tr>
                  );
                })}
                
                {/* Total Row */}
                <tr className="bg-gray-50">
                  <td colSpan={2} className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    Total
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-right text-green-700">
                    {formatarMoeda(standard.custoTotal)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-right text-amber-700">
                    {formatarMoeda(premium.custoTotal)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-right text-blue-700">
                    +{percentual}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparacaoPrecos;